import {
  supabaseClient,
} from "../../services/supabase";

import {
  loadMovimentosPeriodo,
  calcularResumoFinanceiro,
} from "../financeiro/financeiro.service";


type DashboardReserva = {
  id?: string;

  cliente?: string;

  court?: string;

  date?: string;

  hour?: number;

  duration?: number;

  price?: number;

  pago?: boolean;
};


function todayISO() {
  const now =
    new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1,
    ).padStart(
      2,
      "0",
    );

  const day =
    String(
      now.getDate(),
    ).padStart(
      2,
      "0",
    );

  return `${year}-${month}-${day}`;
}


function normalizarHora(
  value: unknown,
) {
  if (
    typeof value ===
    "number"
  ) {
    return value;
  }

  if (
    typeof value !==
    "string"
  ) {
    return 0;
  }

  const [
    horas,
    minutos,
  ] =
    value.split(":");

  return (
    Number(
      horas ?? 0,
    ) +
    Number(
      minutos ?? 0,
    ) /
      60
  );
}


export async function loadDashboardData() {
  const today =
    todayISO();


  /*
   * =====================================================
   * BUSCAS PRINCIPAIS
   * =====================================================
   */

  const [
    movimentosHoje,

    comandasResult,

    estoqueResult,

    reservasResult,

    quadrasResult,
  ] =
    await Promise.all([
      loadMovimentosPeriodo(
        today,
        today,
      ),

      supabaseClient
        .from(
          "comandas",
        )
        .select(
          "*",
        )
        .eq(
          "status",
          "aberta",
        ),

      supabaseClient
        .from(
          "estoque",
        )
        .select(
          "*",
        )
        .eq(
          "ativo",
          true,
        ),

      supabaseClient
        .from(
          "reservas_quadras",
        )
        .select(
          "*",
        )
        .eq(
          "data",
          today,
        ),

      supabaseClient
        .from(
          "quadras",
        )
        .select(
          "id, nome",
        ),
    ]);


  /*
   * =====================================================
   * TRATAMENTO DE ERROS
   * =====================================================
   */

  if (
    comandasResult.error
  ) {
    throw comandasResult.error;
  }


  if (
    estoqueResult.error
  ) {
    throw estoqueResult.error;
  }


  if (
    reservasResult.error
  ) {
    throw reservasResult.error;
  }


  if (
    quadrasResult.error
  ) {
    throw quadrasResult.error;
  }


  /*
   * =====================================================
   * FINANCEIRO
   * =====================================================
   */

  const resumoFinanceiro =
    calcularResumoFinanceiro(
      movimentosHoje,
    );


  /*
   * =====================================================
   * COMANDAS ABERTAS
   * =====================================================
   */

  const comandas =
    (comandasResult.data ?? [])
      .filter(
        (comanda) =>
          (comanda.tipo ?? "venda") === "venda",
      );


  const comandasAbertas =
    comandas.length;


  const valorComandasAbertas =
    comandas.reduce(
      (
        total,
        comanda,
      ) =>
        total +
        Number(
          comanda.total ??
            0,
        ),
      0,
    );


  /*
   * =====================================================
   * ESTOQUE
   * =====================================================
   */

  const estoque =
    estoqueResult.data ??
    [];


  const lowStock =
    estoque.filter(
      (produto) =>
        Number(
          produto.quantidade ??
            0,
        ) <=
        Number(
          produto.estoque_minimo ??
            0,
        ),
    );


  /*
   * =====================================================
   * QUADRAS
   * =====================================================
   */

  const quadras =
    quadrasResult.data ??
    [];


  const quadraNomePorId =
    new Map<
      string,
      string
    >();


  quadras.forEach(
    (quadra) => {
      if (
        quadra.id
      ) {
        quadraNomePorId.set(
          String(
            quadra.id,
          ),

          String(
            quadra.nome ??
              "Quadra",
          ),
        );
      }
    },
  );


  /*
   * =====================================================
   * RESERVAS DE HOJE
   * =====================================================
   */

  const reservas =
    reservasResult.data ??
    [];


  const reservasPagasHoje =
    reservas.filter(
      (reserva) =>
        Boolean(
          reserva.pago,
        ),
    ).length;


  const reservasPendentesHoje =
    reservas.filter(
      (reserva) =>
        !Boolean(
          reserva.pago,
        ),
    ).length;


  /*
   * =====================================================
   * PRÓXIMAS RESERVAS
   * =====================================================
   *
   * Mantemos o formato antigo esperado pelo Dashboard.tsx,
   * mas agora alimentado pelas tabelas reais.
   * =====================================================
   */

  const proximasReservas:
    DashboardReserva[] =
    reservas
      .map(
        (
          reserva,
        ) => {
          const quadraId =
            reserva.quadra_id
              ? String(
                  reserva.quadra_id,
                )
              : "";


          return {
            id:
              reserva.id
                ? String(
                    reserva.id,
                  )
                : undefined,

            cliente:
              String(
                reserva.cliente ??
                  reserva.cliente_nome ??
                  "Cliente",
              ),

            court:
              quadraNomePorId.get(
                quadraId,
              ) ??
              String(
                reserva.quadra_nome ??
                  reserva.quadra ??
                  "Quadra",
              ),

            date:
              String(
                reserva.data ??
                  today,
              ),

            hour:
              normalizarHora(
                reserva.hora ??
                  reserva.horario,
              ),

            duration:
              Number(
                reserva.duracao ??
                  0,
              ),

            price:
              Number(
                reserva.valor ??
                  0,
              ),

            pago:
              Boolean(
                reserva.pago,
              ),
          };
        },
      )
      .sort(
        (
          a,
          b,
        ) =>
          Number(
            a.hour ??
              0,
          ) -
          Number(
            b.hour ??
              0,
          ),
      )
      .slice(
        0,
        5,
      );


  /*
   * =====================================================
   * RETORNO
   * =====================================================
   */

  return {
    saldo:
      resumoFinanceiro.saldo,

    entradasHoje:
      resumoFinanceiro.entradas,

    saidasHoje:
      resumoFinanceiro.saidas,


    comandasAbertas,

    valorComandasAbertas,


    reservasHoje:
      reservas.length,

    reservasPagasHoje,

    reservasPendentesHoje,


    estoqueBaixo:
      lowStock.length,


    proximasReservas,
  };
}
